import { useState, useEffect } from 'react';
import api from '../services/api';

/**
 * Simple hook to manage Jira space selection
 * - Loads project's saved jiraProjectKey on mount
 * - Saves jiraProjectKey when changed
 * 
 * @param {string} projectId - Project ID
 * @param {object} project - Optional project object (avoids duplicate fetch if provided)
 */
export function useJiraSpaces(projectId, project = null) {
  const [jiraSpaces, setJiraSpaces] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState('');
  const [loadingSpaces, setLoadingSpaces] = useState(false);

  // Load spaces and saved jiraProjectKey on mount
  useEffect(() => {
    if (!projectId) return;
    
    const load = async () => {
      setLoadingSpaces(true);
      try {
        // Fetch spaces (project data provided by parent to avoid duplicate fetch)
        const spacesData = await api.get(`/projects/${projectId}/jira/spaces`);
        
        const spaces = spacesData.spaces || [];
        setJiraSpaces(spaces);
        
        // Use provided project data or fetch if not provided
        let projectData = project;
        if (!projectData) {
          const projectResponse = await api.get(`/projects/${projectId}`);
          projectData = projectResponse.project || projectResponse;
        }
        
        const savedSpace = projectData.jiraProjectKey;
        console.log('📍 Loaded jiraProjectKey from project:', savedSpace);
        
        if (savedSpace) {
          setSelectedSpace(savedSpace);
          console.log('✅ Set dropdown to:', savedSpace);
        } else if (spaces.length > 0) {
          // No saved space, auto-select first one
          const firstSpace = spaces[0].key;
          setSelectedSpace(firstSpace);
          console.log('⚠️  No saved space, defaulting to:', firstSpace);
          
          // Save it
          await api.put(`/projects/${projectId}`, { jiraProjectKey: firstSpace });
          console.log('💾 Saved default space to project');
        }
      } catch (error) {
        console.error('Error loading Jira spaces:', error);
      } finally {
        setLoadingSpaces(false);
      }
    };
    
    load();
  }, [projectId, project]);

  // Save space selection to project
  const handleSpaceChange = async (newSpace, onSuccess) => {
    console.log('🔄 Space change requested:', selectedSpace, '→', newSpace);
    
    if (newSpace === selectedSpace) {
      console.log('⏭️  Already selected, skipping');
      return;
    }
    
    const oldSpace = selectedSpace;
    
    // Update UI immediately
    setSelectedSpace(newSpace);
    
    try {
      // Save to backend
      await api.put(`/projects/${projectId}`, { jiraProjectKey: newSpace });
      console.log('✅ Saved jiraProjectKey to project:', newSpace);
      
      // Call success callback (reload tickets)
      if (onSuccess) {
        await onSuccess(newSpace);
      }
    } catch (error) {
      console.error('❌ Failed to save jiraProjectKey:', error);
      // Revert on error
      setSelectedSpace(oldSpace);
    }
  };

  return {
    jiraSpaces,
    selectedSpace,
    loadingSpaces,
    handleSpaceChange
  };
}
